import { BaseModule, INJECTION_TOKEN, SYSTEMS_TOKEN } from '@modules/core';

import { CameraTargetSystem } from './ecs/system/CameraTargetSystem';
import { LODSystem } from './ecs/system/LODSystem';
import { TransformSystem } from './ecs/system/TransformSystem';
import { GraphicAPI } from './graphic';
import { EnvironmentLoader } from './loader/EnvironmentLoader';
import { AsyncTextureLoader } from './loader/extensions/AsyncTextureLoader';
import { StandardExtensionLoader } from './loader/extensions/StandardExtensionLoader';
import { ZEDExtensionLoaderManager } from './loader/extensions/ZEDExtensionLoaderManager';
import { GLTFLoader } from './loader/GLTFLoader';
import { PerformanceProfiler } from './manager/PerformanceProfiler';
import { RenderManager } from './manager/RenderManager';
import {
  clearCommonObservable,
  onAllModelLoaded,
  onFirstModelLoaded,
  onModelLoadProgress,
  onResize,
  onUpdatePostprocessOptionsFromJson,
} from './observable';

export class StandardModule implements BaseModule {
  static readonly INJECTABLE_TOKEN = Symbol.for('StandardModule');
  static readonly [INJECTION_TOKEN] = [
    GLTFLoader,
    EnvironmentLoader,
    ZEDExtensionLoaderManager,
    StandardExtensionLoader,
    AsyncTextureLoader,
    RenderManager,
    GraphicAPI,
    PerformanceProfiler,
  ];
  static readonly [SYSTEMS_TOKEN] = [CameraTargetSystem, TransformSystem, LODSystem];

  readonly api;

  constructor({
    [GLTFLoader.INJECTABLE_TOKEN]: gltfLoader,
    [EnvironmentLoader.INJECTABLE_TOKEN]: environmentLoader,
    [ZEDExtensionLoaderManager.INJECTABLE_TOKEN]: extensionManager,
    [StandardExtensionLoader.INJECTABLE_TOKEN]: extensionLoader,
    [AsyncTextureLoader.INJECTABLE_TOKEN]: asyncTextureLoader,
    [RenderManager.INJECTABLE_TOKEN]: renderManager,
    [GraphicAPI.INJECTABLE_TOKEN]: graphicAPI,
    [PerformanceProfiler.INJECTABLE_TOKEN]: performanceProfiler,
  }: {
    [GLTFLoader.INJECTABLE_TOKEN]: GLTFLoader;
    [EnvironmentLoader.INJECTABLE_TOKEN]: EnvironmentLoader;
    [ZEDExtensionLoaderManager.INJECTABLE_TOKEN]: ZEDExtensionLoaderManager;
    [StandardExtensionLoader.INJECTABLE_TOKEN]: StandardExtensionLoader;
    [AsyncTextureLoader.INJECTABLE_TOKEN]: AsyncTextureLoader;
    [RenderManager.INJECTABLE_TOKEN]: RenderManager;
    [GraphicAPI.INJECTABLE_TOKEN]: GraphicAPI;
    [PerformanceProfiler.INJECTABLE_TOKEN]: PerformanceProfiler;
  }) {
    this.api = {
      renderUpdate: renderManager.renderUpdate.bind(renderManager),
      runRenderLoop: renderManager.runRenderLoop.bind(renderManager),
      stopRenderLoop: renderManager.stopRenderLoop.bind(renderManager),
      loadModels: gltfLoader.loadModels.bind(gltfLoader),
      loadGltf: gltfLoader.loadGltf.bind(gltfLoader),
      observer: {
        onFirstModelLoaded: onFirstModelLoaded,
        onAllModelLoaded: onAllModelLoaded,
        onModelLoadProgress: onModelLoadProgress,
        onResize: onResize,
        onUpdatePostprocessOptionsFromJson: onUpdatePostprocessOptionsFromJson,
      },
      graphic: {
        updatePostprocessOptions: graphicAPI.updatePostprocessOptions.bind(graphicAPI),
        getPostprocessOptions: graphicAPI.getPostprocessOptions.bind(graphicAPI),
        getInitPostprocessOptions: graphicAPI.getInitPostprocessOptions.bind(graphicAPI),
        getResolution: graphicAPI.getResolution.bind(graphicAPI),
        setResolution: graphicAPI.setResolution.bind(graphicAPI),
      },
      performance: performanceProfiler,
    };
  }

  dispose() {
    clearCommonObservable();
  }
}
